import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessSubscriptionContext = createContext();

export const useBusinessSubscription = () => {
  const context = useContext(BusinessSubscriptionContext);
  if (!context) {
    throw new Error('useBusinessSubscription must be used within a BusinessSubscriptionProvider');
  }
  return context;
};

export const BusinessSubscriptionProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business subscription endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getSubscriptionBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-subscriptions/api/v1/business-subscriptions`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-subscriptions/api/v1/business-subscriptions`;
    } else {
      throw new Error('Invalid login type for business subscriptions access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business subscriptions');
    }
  }, [loginType]);

  /**
   * Fetches the full subscription context for a business by its registry number.
   * @param {string} registryNumber The business registry number.
   * @returns {Promise<object>} The full subscription context data.
   */
  const getSubscriptionByRegistryNumber = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubscriptionBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      
      const response = await makeAuthenticatedRequest(url);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch subscription';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubscriptionBaseUrl]);

    /**
   * Fetches the billing history for a business by its registry number.
   * @param {string} registryNumber The business registry number.
   * @returns {Promise<object>} An object containing the billing history array.
   */
  const getBillingHistory = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubscriptionBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/history`;
      
      const response = await makeAuthenticatedRequest(url);
      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch billing history';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubscriptionBaseUrl]);

  /**
   * Health check for the business subscription service.
   * @returns {Promise<object>} The health status of the service.
   */
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      // The health endpoint is at the root of the service, not within the API path
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/business-subscriptions/health`
        : `${API_BASE_URL}/admin/business-subscriptions/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  /**
   * Formats raw subscription data for easier display in the UI.
   * @param {object} subscriptionData The raw subscription data from the API.
   * @returns {object} A formatted object with user-friendly fields.
   */
  const formatSubscriptionData = useCallback((subscriptionData) => {
    if (!subscriptionData) return null;

    const providerDetails = subscriptionData.stripe_details || subscriptionData.paypal_details || {};
    const isStripe = !!subscriptionData.stripe_details;
    const amount = isStripe ? providerDetails.current_amount : providerDetails.plan_amount;

    const formatCurrency = (value, currency) => {
      if (typeof value !== 'number' || !currency) return 'N/A';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(value / 100); // Assuming amount is in cents
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };
    
    const daysRemaining = (endDate) => {
        if (!endDate) return null;
        const diff = new Date(endDate) - new Date();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    return {
      ...subscriptionData,
      provider: subscriptionData.subscription_provider || 'N/A',
      tier: subscriptionData.subscription_tier || 'N/A',
      status: subscriptionData.subscription_status || 'N/A',
      renews: subscriptionData.subscription_renews,
      billingEmail: subscriptionData.billing_email || providerDetails.payer_email || 'N/A',
      
      // Provider-specific details
      providerId: providerDetails.stripe_subscription_id || providerDetails.paypal_subscription_id,
      formattedAmount: formatCurrency(amount, providerDetails.currency),
      billingInterval: providerDetails.billing_interval || 'N/A',
      
      // Dates
      periodStart: formatDate(providerDetails.current_period_start),
      periodEnd: formatDate(providerDetails.current_period_end),
      trialEnd: formatDate(providerDetails.trial_end),
      canceledAt: formatDate(providerDetails.canceled_at),
      
      // Calculated values
      daysRemainingInPeriod: daysRemaining(providerDetails.current_period_end),
      isTrialing: providerDetails.trial_end && new Date(providerDetails.trial_end) > new Date(),
      willCancel: providerDetails.cancel_at_period_end,

      // UI Helpers
      statusColor: subscriptionData.subscription_status === 'active' ? 'green' :
                   subscriptionData.subscription_status === 'canceled' ? 'red' :
                   subscriptionData.subscription_status === 'past_due' ? 'orange' : 'gray',
    };
  }, []);

  /**
   * Fetches and formats a summary of the business's subscription.
   * @param {string} registryNumber The business registry number.
   * @returns {Promise<object>} A formatted summary object.
   */
  const getSubscriptionSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const data = await getSubscriptionByRegistryNumber(registryNumber);
      const formattedData = formatSubscriptionData(data);
      
      return {
        registryNumber: data.business_registry_number,
        provider: formattedData.provider,
        tier: formattedData.tier,
        status: formattedData.status,
        renewsOn: formattedData.periodEnd,
        amount: formattedData.formattedAmount,
        daysRemaining: formattedData.daysRemainingInPeriod,
        willCancel: formattedData.willCancel,
        isTrialing: formattedData.isTrialing,
        hasSubscription: !!data.id,
        accessLevel: loginType,
        statusColor: formattedData.statusColor,
      };

    } catch (err) {
      const errorMessage = err.message || 'Failed to get subscription summary';
      setError(errorMessage);
      // Handle "not found" gracefully for summaries
      if (err.message.includes('not found')) {
        return { registryNumber, hasSubscription: false, status: 'Not Found' };
      }
      throw new Error(errorMessage);
    }
  }, [getSubscriptionByRegistryNumber, formatSubscriptionData, validateAccess, loginType]);

  /**
   * Gets current endpoint info for debugging purposes.
   * @returns {object} Information about the current API endpoint configuration.
   */
  const getEndpointInfo = useCallback(() => {
    const baseUrl = getSubscriptionBaseUrl();
    return {
      loginType,
      baseUrl: baseUrl,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
      endpoints: {
        getSubscription: `${baseUrl}/registry/{registryNumber}`,
        healthCheck: loginType === 'master' 
            ? `${API_BASE_URL}/master/business-subscriptions/health`
            : `${API_BASE_URL}/admin/business-subscriptions/health`,
      }
    };
  }, [loginType, API_BASE_URL, getSubscriptionBaseUrl]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getSubscriptionByRegistryNumber,
    getBillingHistory,
    
    // Utility Actions
    checkHealth,
    getSubscriptionSummary,
    
    // Helpers
    clearError,
    formatSubscriptionData,
    getEndpointInfo,
    
    // Computed values from auth context
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasSubscriptionAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessSubscriptionContext.Provider value={value}>
      {children}
    </BusinessSubscriptionContext.Provider>
  );
};